import React from 'react'
import {useTranslation} from 'react-i18next'

import {Grid, Typography, useTheme} from '@mui/material'
import {makeStyles} from 'tss-react/mui'

type FollowingContentMode =
  | 'contentOnTop'
  | 'contentOnTopWide'
  | 'contentFollowing'

interface ConsultationOverlayHeaderProps {
  title?: string | null
  description?: string | null
  colors?: any
  followingContentMode?: FollowingContentMode
}

const getMinHeight = (title?: string | null, description?: string | null) => {
  if (title && description) return 457
  if (title) return 320
  return 212
}

const getMarginBottom = (mode: FollowingContentMode) => {
  switch (mode) {
    case 'contentOnTop':
      return -40
    case 'contentOnTopWide':
      return -160
    case 'contentFollowing':
    default:
      return 64
  }
}

const useStyles = makeStyles<{
  colors?: any
  minHeight: number
  marginBottom: number
}>()((theme, {colors, minHeight, marginBottom}) => ({
  consultationOverlay: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    minHeight,
    paddingTop: 187,
    paddingBottom: '20px',
    justifyContent: 'flex-start',
    backgroundColor: colors.primary,
    // Break out of parent Container to span full viewport width
    width: '100vw',
    marginLeft: 'calc(-50vw + 50%)',
    marginBottom,
    position: 'relative',
    zIndex: -1,
  },
}))

const ConsultationOverlayHeader: React.FC<ConsultationOverlayHeaderProps> = ({
  title,
  description,
  colors,
  followingContentMode = 'contentFollowing',
}) => {
  const minHeight = getMinHeight(title, description)
  const marginBottom = getMarginBottom(followingContentMode)
  const {classes} = useStyles({colors, minHeight, marginBottom})
  const theme = useTheme()
  const {t} = useTranslation()

  return (
    <Grid className={classes.consultationOverlay}>
      {title && (
        <Typography
          variant='h3'
          color={theme.palette.other.brandColors.white}
          sx={{
            maxWidth: '640px',
            width: '100%',
            px: '20px',
            boxSizing: 'border-box',
          }}
          textAlign={'center'}
          mb={description ? '24px' : 0}
        >
          {t(title)}
        </Typography>
      )}
      {description && (
        <Typography
          variant='body1'
          color={theme.palette.other.brandColors.white}
          sx={{
            maxWidth: '640px',
            width: '100%',
            px: '20px',
            boxSizing: 'border-box',
          }}
          textAlign={'center'}
        >
          {t(description)}
        </Typography>
      )}
    </Grid>
  )
}

export default ConsultationOverlayHeader
